import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import get from "lodash/get";
import lround from "lodash/round";
import { CustomParametersSets } from "../../other-exercise/CustomParametersSets";
import SvgIcon from "../../SvgIcon";
import { repsValue } from "../../../utils/reps-range";
import { rpeValue } from "../../../utils/rpe-range";
import { getRestTime } from "../../../utils/converters/time-helper";
import { getLoadDrop } from "../../../utils/load-drop";

const AddExerciseSets = ({
  selectedExercise,
  primary,
  sets,
  locals,
  handleReorderSet,
  handleSetEditModeToggle,
  handleRemoveSet,
  editSetBackup,
  intensityMeasure,
  isRiR,
  exerciseType,
  secondaryRepMaximum,
  activeUser,
  isEditing,
  activeUserToRequest,
  defaultRepMax,
  // { index, token } of the set group that was just committed; flashes briefly.
  highlight,
}) => {
  const { t } = useTranslation("coaching-portal");
  useEffect(() => {
    const setList = document.querySelector(".set-list");
    if (setList) {
      setList.scrollTop = setList.scrollHeight;
    }
  }, [sets.length]);
  if (!sets?.length > 0) {
    return (
      <div className="set-list no-sets">
        <p className="s1">{t("addExerciseModal.noSets")}</p>
        <p className="p4 mt-2">{t("addExerciseModal.fillRowHint")}</p>
      </div>
    );
  }

  // if there are sets and the exercise has parameters, display custom parameters
  if (primary === "assistant" && selectedExercise?.parameters) {
    return (
      <CustomParametersSets
        selectedExercise={selectedExercise}
        sets={sets}
        locals={locals}
        handleReorderSet={handleReorderSet}
        handleSetEditModeToggle={handleSetEditModeToggle}
        handleRemoveSet={handleRemoveSet}
        editSetBackup={editSetBackup}
        intensityMeasure={intensityMeasure}
        isRiR={isRiR}
        isEditing={isEditing}
      />
    );
  }
  const isAssistant = exerciseType === "assistant";
  // if there are sets and no parameters, display default parameters (sets, reps, rpe, load)
  return (
    <div>
      <div className="set-labels">
        <p className="invisible">-</p>
        <p className="p4">{t("addExerciseModal.sets")}</p>
        <p className="p4">{t("addExerciseModal.reps")}</p>
        <p className="p4">{intensityMeasure}</p>
        <p className="p4">{t("addExerciseModal.load")}</p>
        <p className="p4">{t("addExerciseModal.intensity")}</p>
        <p className="p4">{t("addExerciseModal.restTime")}</p>
        <p className="invisible">-</p>
      </div>
      <div className="set-list">
        {sets.map((set, index) => {
          const equippedRepMax = set.equippedRepMax;

          let repMax = 0;
          if (exerciseType === "primary") {
            repMax = get(set, "equipped", false)
              ? equippedRepMax
              : defaultRepMax;
          } else if (exerciseType === "secondary") {
            repMax = secondaryRepMaximum;
          }
          // repMax(es) are already in KG so no need conversion
          // assumption is set.load is always in KG, see also this.handleNew/EditSetChange. const load below is for display in user's units
          const rawLoad = set.percentRepMax
            ? lround((set.percentRepMax / 100) * repMax, 2)
            : set.load;

          const load = locals.fromMetric(rawLoad, activeUser.units);
          const percentRM =
            load && repMax > 0
              ? lround((rawLoad / repMax) * 100, 1) // Calculate percentRM based on the calculated load and repMax
              : 0; // Default to 0 if load or repMax is invalid
          const hasLoad = Boolean(load);
          const editMode = set.editMode || false;
          const notEditing = isEditing && !editMode;
          const highlighted = highlight?.index === index;

          return (
            <Fragment
              key={`add-exercise-set-${index}${
                highlighted ? `-${highlight.token}` : ""
              }`}
            >
              <div
                className={`Add-Exercise-Set${
                  notEditing ? " not-editing" : ""
                }${highlighted ? " commit-highlight" : ""}`}
                data-order={index}
              >
                <div
                  className={`!flex items-center gap-2${
                    sets.length <= 1 ? " invisible" : ""
                  } mobile-hidden`}
                >
                  <button
                    data-button-type="secondary"
                    disabled={index === sets.length - 1 || isEditing}
                    onClick={(e) => {
                      e.preventDefault();
                      handleReorderSet(1, index);
                    }}
                  >
                    <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
                  </button>
                  <button
                    data-button-type="secondary"
                    disabled={index === 0 || isEditing}
                    onClick={(e) => {
                      e.preventDefault();
                      handleReorderSet(-1, index);
                    }}
                  >
                    <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
                  </button>
                </div>

                <p>{lround(set.sets)}</p>
                <p>
                  {repsValue(set.reps, set.repsLower)}
                  {set.repsTwo && `+${set.repsTwo}`}
                  {set.repsThree && `+${set.repsThree}`}
                </p>
                <p>{rpeValue(set.rpe, set.rpeLower, isRiR)}</p>
                <p>
                  {set.loadDrop
                    ? getLoadDrop(set.loadDrop)
                    : hasLoad
                      ? locals.withWeight(lround(load, 2), activeUser.units)
                      : "-"}
                </p>
                <p>{hasLoad && !isAssistant ? `${percentRM}%` : "-"}</p>
                <p>{getRestTime(set, activeUserToRequest)}</p>

                <div className="flex items-center gap-2">
                  <button
                    data-button-type="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSetEditModeToggle(index);
                    }}
                    className={isEditing ? "invisible" : ""}
                  >
                    <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                  </button>
                  <button
                    data-button-type="secondary"
                    onClick={(e) => {
                      e.preventDefault();
                      if (editMode) {
                        handleSetEditModeToggle(index, editSetBackup);
                        return;
                      }
                      handleRemoveSet(index);
                    }}
                    className={notEditing ? "invisible" : ""}
                  >
                    <SvgIcon
                      src={
                        editMode
                          ? "/img/new-icons/red-cross.svg"
                          : "/img/new-icons/red-trash.svg"
                      }
                      noFilterOnLight
                    />
                  </button>
                </div>
              </div>
              <div
                className={`${
                  sets.length <= 1 ? " invisible" : ""
                } only-show-mobile`}
                role="order-buttons-wrapper"
              >
                <button
                  data-button-type="secondary"
                  disabled={index === sets.length - 1 || isEditing}
                  onClick={(e) => {
                    e.preventDefault();
                    handleReorderSet(1, index);
                  }}
                >
                  <SvgIcon src="/img/new-icons/arrow-square-down.svg" />
                </button>
                <button
                  data-button-type="secondary"
                  disabled={index === 0 || isEditing}
                  onClick={(e) => {
                    e.preventDefault();
                    handleReorderSet(-1, index);
                  }}
                >
                  <SvgIcon src="/img/new-icons/arrow-square-up.svg" />
                </button>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default AddExerciseSets;
